import { type Dispatch } from 'redux';

import { plural } from '@neptune/shared/common-util';
import {
  archiveEntities,
  getEntityCustomId,
  listEntities,
  unarchiveEntities,
} from '@neptune/shared/entity-domain';
import { makeEntityCustomIDidentifier } from '@neptune/shared/entity-util';

import { KnownAttributes } from 'domain/experiment/attribute';
import { addNotification } from 'state/ui/global/notification-popup/actions';
import { deselectAllLeaderboardEntries } from 'state/ui/leaderboards/selection/actions';
import { showServerErrorModal } from 'state/ui/modals/server-error/actions';

function getNotificationText(action: 'archive' | 'unarchive', amount: number): string {
  const runs = plural(amount, { one: 'Run', other: 'Runs' });
  const actionText = action === 'archive' ? 'archive' : 'restore';
  return `${runs} ${actionText}d successfully, this action does not take effect immediately, please refresh the page in a short while to see the changes.`;
}

async function performArchiveAction(
  projectIdentifier: string,
  action: 'archive' | 'unarchive',
  isBulk: boolean,
  shortIds: string[],
  dispatch: Dispatch,
) {
  const entries = await listEntities(
    {
      projectIdentifier,
      runsIds: shortIds,
      attributePathsFilter: [KnownAttributes.CustomRunId],
    },
    {
      filterArchived: false,
      flattenEntities: true,
    },
  );
  const successText = getNotificationText(action, shortIds.length);

  try {
    const actionFn = action === 'archive' ? archiveEntities : unarchiveEntities;
    await actionFn(
      entries.map((entry) =>
        makeEntityCustomIDidentifier(
          entry.organizationName,
          entry.projectName,
          getEntityCustomId(entry),
        ),
      ),
    );
    dispatch(addNotification('success', successText));

    if (isBulk) {
      dispatch(deselectAllLeaderboardEntries());
    }
  } catch {
    showServerErrorModal();
  }
}

export function performBulkArchiveEntities(
  projectIdentifier: string,
  shortIds: string[],
  dispatch: Dispatch,
) {
  return performArchiveAction(projectIdentifier, 'archive', true, shortIds, dispatch);
}

export function performBulkUnarchiveEntities(
  projectIdentifier: string,
  shortIds: string[],
  dispatch: Dispatch,
) {
  return performArchiveAction(projectIdentifier, 'unarchive', true, shortIds, dispatch);
}

export function performArchiveEntity(
  projectIdentifier: string,
  shortId: string,
  dispatch: Dispatch,
) {
  return performArchiveAction(projectIdentifier, 'archive', false, [shortId], dispatch);
}

export function performUnarchiveEntity(
  projectIdentifier: string,
  shortId: string,
  dispatch: Dispatch,
) {
  return performArchiveAction(projectIdentifier, 'unarchive', false, [shortId], dispatch);
}
